import { createCheckoutSession, getCheckoutSessionFailure, resolveCheckoutEmailForAttempt } from "./api/checkout";
import { getMetaTrackingContext, rememberPendingCheckoutTracking, trackMetaInitiateCheckout } from "./analytics/metaPixel";
import { getCheckoutIdentity, getReadyCheckoutUrl, handoffCheckout, type ReadyCheckout } from "./funnel/checkoutHandoff";
import { getCheckoutOfferPlans } from "./funnel/pricingOffer";
import { usePostHog } from "@posthog/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FunnelShell } from "./components/FunnelShell";
import { FunnelLoadingState } from "./components/ui/FunnelLoadingState";
import { StepRenderer, preloadStepRendererChunk } from "./components/StepRenderer";
import { BottomConfirmBar } from "./components/ui/BottomConfirmBar";
import { TopBar } from "./components/ui/TopBar";
import { getImagePreloadCandidatesForSources } from "./components/imageMeta";
import {
  computeSectionProgress,
  getFlowSteps,
  getFunnelSections,
  resolvePersonaKey
} from "./funnel/flowData";
import { replaceStepInUrl, resolveStepFromUrl } from "./funnel/resolveStepFromUrl";
import { resolveWelcomeVariantFromUrl } from "./funnel/resolveFlowFromUrl";
import {
  resolveCheckoutProviderFromUrl,
  resolveExplicitCheckoutProviderFromUrl
} from "./funnel/checkoutProvider";
import { useFunnelState } from "./funnel/useFunnelState";
import { resolveLiveMonthlyOfferForTracking, useLivePrices } from "./funnel/useLivePrices";
import { buildFunnelImageRepository } from "./perf/funnelImageRepository";
import { startPhasedImagePreloadOnce } from "./perf/preloadImages";
import { getCheckoutImagePreloadUrls } from "./perf/checkoutImageAssets";
import {
  buildEmbeddedStepPageviewPayload,
  FLOW_STORAGE_KEY,
  getOrCreateFunnelSessionId,
  resolveInitialFlowState
} from "./analytics/funnelTracking";
import { getEmailAttribution } from "./analytics/emailAttribution";
import { logMetaDedup } from "./analytics/metaDedupDebug";
import {
  buildPaywallPersonalization,
  derivePaywallScenario,
  resolvePaywallQaContextFromUrl,
  type PaywallVariant
} from "./funnel/paywallPersonalization";
import {
  clearPendingRecoveryCode,
  readPendingRecoveryCode,
  resolveLocalRecoveryPreview,
  type RecoveryContext
} from "./funnel/recoveryContext";
import { resolvePaywallEntryContextFromUrl } from "./funnel/paywallEntryPoint";

const PERSONALIZED_PAYWALL_FLAG = "personalized-14-day-paywall";

type IdleDeadlineLike = {
  didTimeout: boolean;
  timeRemaining: () => number;
};

type ScheduledTaskHandle =
  | { type: "idle"; id: number }
  | { type: "timeout"; id: number };

type WindowWithStartupFlags = Window &
  typeof globalThis & {
    __VIREO_LAST_PAGEVIEW_URL__?: string;
    __VIREO_WELCOME_SHELL_CONTINUE__?: boolean;
    __VIREO_WELCOME_SHELL_CAROUSEL_TIMER__?: number;
    __VIREO_WELCOME_SHELL_PENDING_ANSWER__?: {
      answerKey: string;
      value: string;
    };
    requestIdleCallback?: (callback: (deadline: IdleDeadlineLike) => void, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

const scheduleIdleTask = (work: () => void, timeout = 1500): ScheduledTaskHandle => {
  const idleWindow = window as WindowWithStartupFlags;

  if (typeof idleWindow.requestIdleCallback === "function") {
    return {
      type: "idle",
      id: idleWindow.requestIdleCallback(
        (deadline) => {
          if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
            work();
            return;
          }

          work();
        },
        { timeout }
      )
    };
  }

  return {
    type: "timeout",
    id: window.setTimeout(work, timeout)
  };
};

const cancelScheduledTask = (task: ScheduledTaskHandle | null): void => {
  if (!task) {
    return;
  }

  if (task.type === "idle") {
    (window as WindowWithStartupFlags).cancelIdleCallback?.(task.id);
    return;
  }

  window.clearTimeout(task.id);
};

const preloadLateStepModules = (): Promise<unknown[]> => {
  return Promise.all([
    import("./api/checkout"),
    import("./api/leads"),
    import("./api/viewContentTrack"),
    import("./funnel/buildOnboardingResponses"),
    import("./analytics/metaPixel")
  ]);
};

const readStoredFlow = (): "a" | "b" | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(FLOW_STORAGE_KEY);
    return stored === "a" || stored === "b" ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredFlow = (flow: "a" | "b"): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(FLOW_STORAGE_KEY, flow);
  } catch {
    // Ignore storage write failures (private mode, disabled storage, etc.).
  }
};

const App = () => {
  const posthog = usePostHog();
  const funnelSessionId = useMemo(() => getOrCreateFunnelSessionId(), []);
  const emailAttribution = useMemo(() => getEmailAttribution(), []);
  const paywallQaContext = useMemo(() => resolvePaywallQaContextFromUrl(), []);
  const paywallEntryContext = useMemo(() => resolvePaywallEntryContextFromUrl(), []);
  const localRecoveryPreview = useMemo(() => resolveLocalRecoveryPreview(), []);
  const recoveryCode = useMemo(() => readPendingRecoveryCode(), []);
  const flow = useMemo(() => {
    const { flow: resolvedFlow, shouldPersistFlow } = resolveInitialFlowState(
      window.location.href,
      readStoredFlow()
    );
    if (shouldPersistFlow) {
      writeStoredFlow(resolvedFlow);
    }
    return resolvedFlow;
  }, []);
  const initialCheckoutProvider = useMemo(() => resolveCheckoutProviderFromUrl(), []);
  const recoveryProviderFallback = useMemo(
    () => resolveExplicitCheckoutProviderFromUrl() ?? "stripe",
    []
  );
  const welcomeVariant = useMemo(() => resolveWelcomeVariantFromUrl(), []);
  const steps = useMemo(() => getFlowSteps(flow, welcomeVariant), [flow, welcomeVariant]);
  const funnelSections = useMemo(() => getFunnelSections(steps), [steps]);
  const initialStepId = useMemo(() => resolveStepFromUrl(), []);
  const {
    answers,
    currentIndex,
    currentStep,
    canGoBack,
    canContinue,
    goBack,
    goNext,
    goToIndex,
    setSingleAnswer,
    toggleMultiAnswer,
    setTextAnswer
  } = useFunnelState(steps, {
    flowKey: `${flow}:${paywallEntryContext.entryPoint}`,
    initialStepId,
    resetPersistedPaymentSelection: !recoveryCode && !localRecoveryPreview && !paywallQaContext.planId
  });
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState<string | null>(null);
  const [readyCheckout, setReadyCheckout] = useState<ReadyCheckout | null>(null);
  const [checkoutErrorCode, setCheckoutErrorCode] = useState<string | null>(null);
  const [checkoutProvider, setCheckoutProvider] = useState(initialCheckoutProvider);
  const [recoveryContext, setRecoveryContext] = useState<RecoveryContext | null>(localRecoveryPreview);
  const [isRecoveryContextLoading, setIsRecoveryContextLoading] = useState(Boolean(recoveryCode));
  const [experimentPaywallVariant, setExperimentPaywallVariant] = useState<PaywallVariant>("control");
  const [isPaywallAssignmentReady, setIsPaywallAssignmentReady] = useState(
    import.meta.env.DEV || paywallEntryContext.entryPoint !== "funnel" ||
      Boolean(recoveryCode || localRecoveryPreview || paywallQaContext.hasOverride)
  );
  const [, setIsWelcomeHeroReady] = useState(true);
  const emailSubmittedRef = useRef(false);
  const emailSubmissionPendingRef = useRef(false);
  const checkoutSubmissionPendingRef = useRef(false);
  const leadIdRef = useRef<number | null>(null);
  const backgroundWarmupStartedRef = useRef(false);
  const autoAdvanceTimeoutRef = useRef<number | null>(null);
  const paywallExposureRef = useRef<string | null>(null);
  const isWelcomeStep = currentStep?.id === "welcome";

  useEffect(() => {
    // Keep browser exposure and server-side paid events on the same anonymous
    // identity so PostHog experiments can measure purchase outcomes directly.
    posthog.identify(funnelSessionId);
  }, [funnelSessionId, posthog]);

  useEffect(() => {
    if (!recoveryCode) return;
    let cancelled = false;
    void import("./api/recovery")
      .then(({ resolveRecoveryContext }) => resolveRecoveryContext(recoveryCode, recoveryProviderFallback))
      .then((context) => {
        if (!cancelled) {
          setCheckoutProvider(context.checkoutProvider);
          setRecoveryContext(context);
        }
      })
      .catch((error) => {
        console.warn("[checkout-recovery] failed to restore context", error);
        clearPendingRecoveryCode();
      })
      .finally(() => {
        if (!cancelled) setIsRecoveryContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recoveryCode, recoveryProviderFallback]);

  useEffect(() => {
    if (paywallEntryContext.entryPoint !== "funnel" || recoveryCode || localRecoveryPreview || paywallQaContext.hasOverride) return;
    const fallbackTimer = window.setTimeout(() => setIsPaywallAssignmentReady(true), 1500);
    const unsubscribe = posthog.onFeatureFlags((_flags, variants) => {
      setExperimentPaywallVariant(
        variants[PERSONALIZED_PAYWALL_FLAG] === "personalized_14d"
          ? "personalized_14d"
          : "control"
      );
      setIsPaywallAssignmentReady(true);
      window.clearTimeout(fallbackTimer);
    });
    return () => {
      window.clearTimeout(fallbackTimer);
      unsubscribe();
    };
  }, [localRecoveryPreview, paywallEntryContext.entryPoint, paywallQaContext.hasOverride, posthog, recoveryCode]);

  useEffect(() => {
    const restoredPlanId = recoveryContext?.planId ?? paywallQaContext.planId;
    if (restoredPlanId) {
      setTextAnswer("selected_plan", restoredPlanId);
    }
  }, [paywallQaContext.planId, recoveryContext, setTextAnswer]);

  const paywallVariant = recoveryContext?.paywallVariant ??
    (paywallQaContext.hasOverride
      ? paywallQaContext.variant
      : paywallEntryContext.entryPoint !== "funnel"
        ? "control"
        : experimentPaywallVariant);
  const livePrices = useLivePrices(checkoutProvider, paywallVariant, recoveryContext?.currency ?? undefined);
  const {
    plans: livePlans,
    resolvedProvider: livePricesProvider
  } = livePrices;
  const derivedScenario = derivePaywallScenario(answers);
  const paywallScenario = recoveryContext?.paywallScenario ??
    (paywallQaContext.hasOverride && paywallQaContext.scenario !== "default"
      ? paywallQaContext.scenario
      : derivedScenario);
  const isStandaloneLifecycleEntry = paywallEntryContext.entryPoint === "lifecycle_email" &&
    !recoveryCode && !localRecoveryPreview && !recoveryContext;
  const pricingOffer = recoveryContext ? "original_checkout_recovery" : "weekly_quarterly_yearly";
  const paywallPersonalization = paywallEntryContext.isAppEntry ||
    isStandaloneLifecycleEntry || paywallVariant === "personalized_14d" ||
    paywallVariant === "annual_trial_3d"
    ? buildPaywallPersonalization({
        variant: paywallVariant,
        scenario: paywallScenario,
        entryPoint: paywallEntryContext.entryPoint,
        isRecovery: Boolean(recoveryContext),
        recommendedPlanId: recoveryContext?.planId ??
          (paywallQaContext.hasOverride ? paywallQaContext.planId : null)
      })
    : null;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("body-welcome-step", isWelcomeStep);

    return () => {
      document.body.classList.remove("body-welcome-step");
    };
  }, [isWelcomeStep]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current !== null) {
        window.clearTimeout(autoAdvanceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const welcomeStep = steps[0];
      if (welcomeStep?.kind === "welcome") {
        startPhasedImagePreloadOnce({
          runKey: `flow:${flow}:welcome-hero`,
          critical: getImagePreloadCandidatesForSources(
            welcomeStep.heroSlides?.map((slide) => slide.image)
          ),
          secondary: [],
          concurrency: 1
        });

        const validationWarnings = buildFunnelImageRepository({
          steps,
          currentIndex: 0,
          lookaheadSteps: 0
        }).warnings;

        if (import.meta.env.DEV && validationWarnings.length > 0) {
          console.warn("[image-preload] validation warnings", validationWarnings);
        }
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [flow, steps]);

  useEffect(() => {
    if (!currentStep || currentStep.kind === "welcome") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const imageRepository = buildFunnelImageRepository({
        steps,
        currentIndex,
        lookaheadSteps: 1
      });

      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:step:${currentStep.id}`,
        critical: imageRepository.critical,
        secondary: imageRepository.secondary,
        concurrency: 2
      });

      if (import.meta.env.DEV && imageRepository.warnings.length > 0) {
        console.warn("[image-preload] validation warnings", imageRepository.warnings);
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, currentStep, flow, steps]);

  const checkoutPreloadTriggerIndex = useMemo(() => {
    const paymentStepIndex = steps.findIndex((step) => step.kind === "payment");
    if (paymentStepIndex < 0) {
      return -1;
    }
    return Math.max(paymentStepIndex - 2, 0);
  }, [steps]);

  useEffect(() => {
    if (checkoutPreloadTriggerIndex < 0 || currentIndex < checkoutPreloadTriggerIndex) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:checkout-images`,
        critical: getCheckoutImagePreloadUrls(),
        secondary: []
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [checkoutPreloadTriggerIndex, currentIndex, flow]);

  useEffect(() => {
    const startupWindow = window as WindowWithStartupFlags;
    if (startupWindow.__VIREO_WELCOME_SHELL_CAROUSEL_TIMER__) {
      window.clearInterval(startupWindow.__VIREO_WELCOME_SHELL_CAROUSEL_TIMER__);
      startupWindow.__VIREO_WELCOME_SHELL_CAROUSEL_TIMER__ = undefined;
    }

    document.documentElement.dataset.appReady = "true";
  }, []);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome") {
      return;
    }

    const startupWindow = window as WindowWithStartupFlags;
    if (!startupWindow.__VIREO_WELCOME_SHELL_CONTINUE__) {
      return;
    }

    startupWindow.__VIREO_WELCOME_SHELL_CONTINUE__ = false;
    const continueTimer = window.setTimeout(() => {
      goNext();
    }, 0);

    return () => {
      window.clearTimeout(continueTimer);
    };
  }, [currentStep, goNext]);

  useEffect(() => {
    const startupWindow = window as WindowWithStartupFlags;
    const pendingAnswer = startupWindow.__VIREO_WELCOME_SHELL_PENDING_ANSWER__;
    if (!pendingAnswer) {
      return;
    }

    startupWindow.__VIREO_WELCOME_SHELL_PENDING_ANSWER__ = undefined;
    setSingleAnswer(pendingAnswer.answerKey, pendingAnswer.value);
  }, [setSingleAnswer]);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome" || backgroundWarmupStartedRef.current) {
      return;
    }

    backgroundWarmupStartedRef.current = true;

    const warmupStartIndex = Math.min(currentIndex + 1, steps.length - 1);
    const postWelcomeImageRepository = buildFunnelImageRepository({
      steps,
      currentIndex: warmupStartIndex,
      lookaheadSteps: 1
    });
    let secondWarmupTask: ScheduledTaskHandle | null = null;

    const firstWarmupTask = scheduleIdleTask(() => {
      void preloadStepRendererChunk(steps[currentIndex + 1]?.kind);

      startPhasedImagePreloadOnce({
        runKey: `flow:${flow}:next-step-images`,
        critical: postWelcomeImageRepository.critical,
        secondary: [],
        concurrency: 1
      });

      secondWarmupTask = scheduleIdleTask(() => {
        const stepKinds = Array.from(new Set(steps.slice(currentIndex + 1).map((step) => step.kind)));

        void Promise.all([
          preloadLateStepModules(),
          ...stepKinds.map((kind) => preloadStepRendererChunk(kind))
        ]);

        startPhasedImagePreloadOnce({
          runKey: `flow:${flow}:background-secondary`,
          critical: [],
          secondary: postWelcomeImageRepository.secondary,
          concurrency: 2
        });
      }, 2500);
    }, 1200);

    return () => {
      cancelScheduledTask(firstWarmupTask);
      cancelScheduledTask(secondWarmupTask);
    };
  }, [currentIndex, currentStep, flow, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    if (currentStep.kind !== "welcome") {
      setIsWelcomeHeroReady(true);
      return;
    }

    setIsWelcomeHeroReady(!(currentStep.heroImage || currentStep.heroVideo));
  }, [currentStep]);

  useEffect(() => {
    if (currentStep?.kind === "welcome") {
      return;
    }

    const nextStep = steps[currentIndex + 1];
    if (!nextStep) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      void preloadStepRendererChunk(nextStep.kind);
      if (currentStep?.kind === "loading") {
        void preloadStepRendererChunk("result");
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, currentStep, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    replaceStepInUrl(currentStep.id);

    const currentUrl = window.location.href;
    const trackingWindow = window as WindowWithStartupFlags;
    if (trackingWindow.__VIREO_LAST_PAGEVIEW_URL__ === currentUrl) {
      return;
    }

    trackingWindow.__VIREO_LAST_PAGEVIEW_URL__ = currentUrl;
    posthog.capture("$pageview", {
      ...emailAttribution,
      $current_url: currentUrl,
      funnel_session_id: funnelSessionId,
      flow_variant: flow,
      funnel_flow: flow,
      welcome_variant: welcomeVariant,
      funnel_hash_route: window.location.hash || `#${currentStep.id}`,
      funnel_step_id: currentStep.id,
      funnel_step_index: currentIndex,
      paywall_entry_point: paywallEntryContext.entryPoint,
      paywall_source: paywallEntryContext.source
    });
  }, [currentIndex, currentStep, flow, welcomeVariant, funnelSessionId, paywallEntryContext, posthog]);

  useEffect(() => {
    if (currentStep?.kind !== "payment" || !isPaywallAssignmentReady) return;
    const exposureKey = `${funnelSessionId}:${paywallEntryContext.entryPoint}:${paywallVariant}:${paywallScenario}`;
    if (paywallExposureRef.current === exposureKey) return;
    paywallExposureRef.current = exposureKey;
    posthog.capture("paywall_exposed", {
      pricing_offer: pricingOffer,
      ...emailAttribution,
      funnel_session_id: funnelSessionId,
      funnel_flow: flow,
      welcome_variant: welcomeVariant,
      paywall_variant: paywallVariant,
      paywall_scenario: paywallScenario,
      paywall_entry_point: paywallEntryContext.entryPoint,
      paywall_source: paywallEntryContext.source,
      is_checkout_recovery: Boolean(recoveryContext)
    });
    if (paywallEntryContext.entryPoint === "funnel" && !recoveryContext && !paywallQaContext.hasOverride) {
      posthog.getFeatureFlag(PERSONALIZED_PAYWALL_FLAG);
    }
  }, [currentStep, flow, funnelSessionId, isPaywallAssignmentReady, paywallEntryContext, paywallQaContext.hasOverride, paywallScenario, paywallVariant, posthog, pricingOffer, recoveryContext]);

  useEffect(() => {
    const onBrowserNavigation = () => {
      const stepIdFromUrl = resolveStepFromUrl();
      if (!stepIdFromUrl) {
        return;
      }

      const nextIndex = steps.findIndex((step) => step.id === stepIdFromUrl);
      if (nextIndex >= 0) {
        goToIndex(nextIndex);
      }
    };

    window.addEventListener("popstate", onBrowserNavigation);
    window.addEventListener("hashchange", onBrowserNavigation);
    return () => {
      window.removeEventListener("popstate", onBrowserNavigation);
      window.removeEventListener("hashchange", onBrowserNavigation);
    };
  }, [goToIndex, steps]);

  if (!currentStep) {
    return null;
  }

  const isRecoveryPricingLoading = Boolean(
    recoveryContext && (
      livePrices.isLoading || livePricesProvider !== recoveryContext.checkoutProvider
    )
  );
  const isTrialOffer = paywallVariant === "annual_trial_3d";
  const isTrialPricingLoading = isTrialOffer && (
    livePrices.isLoading || livePricesProvider !== checkoutProvider
  );
  if (
    (isRecoveryContextLoading || isRecoveryPricingLoading || isTrialPricingLoading || !isPaywallAssignmentReady) &&
    currentStep.kind === "payment"
  ) {
    return (
      <div className="app-frame">
        <FunnelShell isScrollable topBar={null}>
          <FunnelLoadingState
            label={isRecoveryContextLoading || isRecoveryPricingLoading ? "Restoring your Sehaj path…" : "Preparing your Sehaj offer…"}
            detail={
              isRecoveryContextLoading || isRecoveryPricingLoading
                ? "Bringing back your saved plan and secure checkout details."
                : "Matching the right plan details for your path."
            }
          />
        </FunnelShell>
      </div>
    );
  }

  if (isTrialOffer && !livePrices.hasLivePrices && currentStep.kind === "payment") {
    return (
      <div className="app-frame">
        <FunnelShell isScrollable topBar={null}>
          <FunnelLoadingState
            label="This trial offer is temporarily unavailable"
            detail="We could not confirm the live price, so we have not shown or started checkout. Please refresh and try again."
          />
        </FunnelShell>
      </div>
    );
  }

  if (recoveryContext && currentStep.kind === "payment" && (
    !livePrices.hasLivePrices ||
    !livePlans.some((plan) => plan.id === recoveryContext.planId) ||
    (recoveryContext.currency && livePrices.resolvedCurrency !== recoveryContext.currency.toUpperCase())
  )) {
    return (
      <div className="app-frame">
        <FunnelShell isScrollable topBar={null}>
          <FunnelLoadingState
            label="Your saved checkout is temporarily unavailable"
            detail="We could not confirm your saved price. Please refresh and try again."
          />
        </FunnelShell>
      </div>
    );
  }

  const resolvedCurrentStep = currentStep.kind === "payment"
    ? { ...currentStep, plans: getCheckoutOfferPlans(livePlans, recoveryContext?.planId) }
    : currentStep;

  const sectionProgress = computeSectionProgress(currentIndex, funnelSections);
  const isPaymentStep = resolvedCurrentStep.kind === "payment";
  const isLoadingStep = resolvedCurrentStep.kind === "loading";
  const isResultStep = resolvedCurrentStep.kind === "result";
  const bottomBarClassName = isWelcomeStep
    ? "bottom-confirm-bar-welcome"
    : isResultStep || resolvedCurrentStep.kind === "email"
      ? "bottom-confirm-bar-polished"
      : undefined;
  /** Welcome without embedded question stays locked (full-viewport hero). Other steps scroll so options and the loading-step modal are reachable on short viewports. */
  const isFunnelContentScrollable =
    resolvedCurrentStep.kind !== "welcome" ||
    (resolvedCurrentStep.kind === "welcome" && !!resolvedCurrentStep.embeddedQuestion);
  const isCheckoutCompleteStep = resolvedCurrentStep.id === "checkout_complete";
  const shouldShowBottomBar =
    !isLoadingStep &&
    !isPaymentStep &&
    !isCheckoutCompleteStep &&
    !(
      resolvedCurrentStep.kind === "question" &&
      resolvedCurrentStep.selection === "single"
    ) &&
    !(isWelcomeStep && resolvedCurrentStep.kind === "welcome" && !!resolvedCurrentStep.embeddedQuestion);
  const confirmLabelText = resolvedCurrentStep.confirmLabel ?? "Continue →";
  const bottomBarNoteAbove =
    resolvedCurrentStep.kind === "welcome"
      ? resolvedCurrentStep.finePrint
      : resolvedCurrentStep.footerNote;
  const bottomBarNoteBelow = undefined;
  const confirmLabel = confirmLabelText;
  const confirmDisabled = !canContinue || isStartingCheckout;
  const disabledHint = undefined;

  const centerAlignedQuestionIds = ["practice_priorities", "your_why", "biggest_blocker"];
  const isCenterableQuestionPage =
    !["saakhi_familiarity", "saakhi_audience"].includes(resolvedCurrentStep.id) &&
    resolvedCurrentStep.kind === "question" &&
    ((resolvedCurrentStep.selection === "single" &&
      resolvedCurrentStep.options.length <= 5 &&
      !resolvedCurrentStep.optionFeedback &&
      !resolvedCurrentStep.helperText) ||
      centerAlignedQuestionIds.includes(resolvedCurrentStep.id));

  const trackWelcomeViewContent = (): void => {
    const monthlyOffer = resolveLiveMonthlyOfferForTracking(livePrices, checkoutProvider);
    if (!monthlyOffer) {
      console.warn("[meta] ViewContent not fired — live regional price unavailable");
      return;
    }
    const { contentId, currency, value } = monthlyOffer;

    void (async () => {
      try {
        const [
          { getMetaTrackingContext, trackMetaViewContent },
          { trackViewContentServerEvent }
        ] = await Promise.all([
          import("./analytics/metaPixel"),
          import("./api/viewContentTrack")
        ]);
        const viewContentCtx = getMetaTrackingContext();
        const event = {
          eventId: viewContentCtx.eventId,
          contentId,
          currency,
          value
        };
        trackMetaViewContent(event);
        await trackViewContentServerEvent({
          ...event,
          fbp: viewContentCtx.fbp,
          fbc: viewContentCtx.fbc
        });
      } catch (error) {
        console.warn("[meta] ViewContent tracking failed", error);
      }
    })();
  };

  const handleConfirm = async () => {
    if (!canContinue || isStartingCheckout) {
      return;
    }

    if (autoAdvanceTimeoutRef.current !== null) {
      window.clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }

    if (resolvedCurrentStep.kind === "welcome") {
      trackWelcomeViewContent();
    }

    if (resolvedCurrentStep.kind === "payment") {
      const selectedPlanId = answers[resolvedCurrentStep.answerKey];
      if (typeof selectedPlanId !== "string") {
        return;
      }

      const selectedPlan = resolvedCurrentStep.plans.find((plan) => plan.id === selectedPlanId);
      if (!selectedPlan) {
        return;
      }

      if (checkoutSubmissionPendingRef.current) return;
      checkoutSubmissionPendingRef.current = true;
      setIsStartingCheckout(true);
      const attemptStartedAt = performance.now();
      try {
        const preparedUrl = getReadyCheckoutUrl(readyCheckout, selectedPlan, answers.email, checkoutProvider);
        if (preparedUrl) {
          window.location.assign(preparedUrl);
          return;
        }
        setReadyCheckout(null);
        const trackingContext = getMetaTrackingContext();
        const emailStepEnabled = steps.some(
          (step) => step.kind === "email" && step.answerKey === "email"
        );
        const email = resolveCheckoutEmailForAttempt(
          answers.email,
          emailStepEnabled,
          checkoutErrorCode
        );
        const planValue = selectedPlan.amount ?? (parseFloat(selectedPlan.totalPrice.replace(/[^0-9.]/g, "")) || 0);
        const planCurrency = selectedPlan.currencyCode ?? "USD";
        const checkoutEventProps = {
          ...emailAttribution,
          funnel_session_id: funnelSessionId,
          funnel_flow: flow,
          welcome_variant: welcomeVariant,
          funnel_step_id: resolvedCurrentStep.id,
          funnel_step_index: currentIndex,
          checkout_plan_id: selectedPlan.id,
          pricing_offer: pricingOffer,
          checkout_plan_value: planValue,
          checkout_currency: planCurrency,
          checkout_provider: checkoutProvider,
          has_email: Boolean(email),
          has_lead_id: leadIdRef.current !== null,
          paywall_variant: paywallVariant,
          paywall_scenario: paywallScenario,
          paywall_entry_point: paywallEntryContext.entryPoint,
          paywall_source: paywallEntryContext.source,
          is_checkout_recovery: Boolean(recoveryContext),
          checkout_trial_days: paywallVariant === "annual_trial_3d" && selectedPlan.id === "annual" ? 3 : 0
        };

        setCheckoutErrorMessage(null);
        setCheckoutErrorCode(null);
        let sessionCreated = false;
        try {
          try {
            posthog.capture("paywall_cta_clicked", checkoutEventProps);
          } catch {
            console.warn("[posthog] paywall_cta_clicked not captured");
          }
          try {
            posthog.capture("checkout_started", checkoutEventProps);
          } catch {
            console.warn("[posthog] checkout_started not captured");
          }

          const session = await createCheckoutSession({
            ...(email ? { email } : {}),
            planId: selectedPlan.id,
            flow,
            provider: checkoutProvider,
            currency: planCurrency,
            value: planValue,
            eventId: trackingContext.eventId,
            ...(leadIdRef.current ? { leadId: leadIdRef.current } : {}),
            ...(trackingContext.fbp ? { fbp: trackingContext.fbp } : {}),
            ...(trackingContext.fbc ? { fbc: trackingContext.fbc } : {}),
            funnelSessionId,
            emailAttribution,
            paywallVariant,
            paywallScenario,
            paywallEntryPoint: paywallEntryContext.entryPoint,
            ...(recoveryCode ? { recoveryCode } : {})
          });
          sessionCreated = true;
          setReadyCheckout({ url: session.checkoutUrl, identity: getCheckoutIdentity(selectedPlan, answers.email, checkoutProvider), createdAt: Date.now() });
          const sessionCreationMs = Math.round(performance.now() - attemptStartedAt);
          const authoritativePlanId = session.planId;
          const authoritativeCurrency = session.currency;
          const authoritativeValue = session.value;
          const authoritativeProvider = session.provider;

          try {
            logMetaDedup("checkout_session_created", {
              source: "browser",
              sessionId: session.sessionId,
              eventId: trackingContext.eventId,
              planId: authoritativePlanId,
              provider: authoritativeProvider,
              currency: authoritativeCurrency,
              value: authoritativeValue
            });
          } catch { /* Debug logging is optional. */ }

          try {
            posthog.capture("checkout_session_created", {
              ...checkoutEventProps,
              checkout_plan_id: authoritativePlanId,
              checkout_plan_value: authoritativeValue,
              checkout_currency: authoritativeCurrency,
              checkout_provider: authoritativeProvider,
              checkout_session_id: session.sessionId,
              checkout_session_creation_ms: sessionCreationMs
            });
          } catch {
            console.warn("[posthog] checkout_session_created not captured");
          }

          const optionalEffects = [
            { name: "flow_storage", run: () => writeStoredFlow(flow) },
            { name: "checkout_storage", run: () => rememberPendingCheckoutTracking({
              sessionId: session.sessionId,
              eventId: trackingContext.eventId,
              planId: authoritativePlanId,
              value: authoritativeValue,
              currency: authoritativeCurrency,
              provider: authoritativeProvider,
              completionToken: session.completionToken
            }) },
            { name: "meta_pixel", run: () => trackMetaInitiateCheckout({
              eventId: trackingContext.eventId,
              contentId: authoritativePlanId,
              currency: authoritativeCurrency,
              value: authoritativeValue
            }) }
          ];

          if (recoveryCode) {
            try {
              posthog.capture("checkout_resumed", {
                ...checkoutEventProps,
                checkout_session_id: session.sessionId,
                lifecycle_attribution_id: recoveryContext?.lifecycleAttributionId ?? "unknown"
              });
            } catch {
              console.warn("[posthog] checkout_resumed not captured");
            }
            clearPendingRecoveryCode();
          }
          handoffCheckout(optionalEffects, () => {
            try {
              posthog.capture(
                authoritativeProvider === "stripe" ? "checkout_redirected_to_stripe" : "checkout_redirected_to_polar",
                {
                  ...checkoutEventProps,
                  checkout_plan_id: authoritativePlanId,
                  checkout_plan_value: authoritativeValue,
                  checkout_currency: authoritativeCurrency,
                  checkout_provider: authoritativeProvider,
                  checkout_session_id: session.sessionId,
                  checkout_session_creation_ms: sessionCreationMs,
                  checkout_redirect_attempt_ms: Math.round(performance.now() - attemptStartedAt)
                }
              );
            } catch {
              console.warn("[posthog] checkout redirect not captured");
            }
            window.location.assign(session.checkoutUrl);
          }, (name) => {
            posthog.capture("checkout_optional_step_failed", {
              ...checkoutEventProps,
              checkout_session_id: session.sessionId,
              optional_step: name
            });
          });
        } catch (error) {
          const failure = sessionCreated ? {
            code: "checkout_redirect_failed", status: 0, retryable: true,
            message: "Your secure checkout is ready. Use Open secure checkout to continue."
          } : getCheckoutSessionFailure(error);
          setCheckoutErrorMessage(failure.message);
          setCheckoutErrorCode(failure.code);
          try {
            posthog.capture(sessionCreated ? "checkout_redirect_failed" : "checkout_session_failed", {
              ...checkoutEventProps,
              error_code: failure.code,
              error_status: failure.status,
              error_retryable: failure.retryable,
              checkout_attempt_duration_ms: Math.round(performance.now() - attemptStartedAt)
            });
          } catch {
            console.warn("[posthog] checkout_session_failed not captured");
          }
          console.warn("[checkout] failed to create checkout session", {
            errorCode: failure.code,
            status: failure.status
          });
        }

        return;
      } catch (error) {
        const failure = getCheckoutSessionFailure(error);
        setCheckoutErrorMessage(failure.message);
        setCheckoutErrorCode(failure.code);
      } finally {
        checkoutSubmissionPendingRef.current = false;
        setIsStartingCheckout(false);
      }
    }

    if (resolvedCurrentStep.kind === "email") {
      if (emailSubmissionPendingRef.current) return;
      emailSubmissionPendingRef.current = true;
      try {
        if (emailSubmittedRef.current) {
          goNext();
          return;
        }
        const email = answers[resolvedCurrentStep.answerKey] as string;
        const [
          { buildOnboardingResponses },
          { saveLeadWithRetry, trackLeadServerEvent },
          { getMetaTrackingContext, trackMetaLead }
        ] = await Promise.all([
          import("./funnel/buildOnboardingResponses"),
          import("./api/leads"),
          import("./analytics/metaPixel")
        ]);
        const onboardingResponses = buildOnboardingResponses(steps, answers);
        const personaKey = resolvePersonaKey(answers);
        const leadTrackingContext = getMetaTrackingContext();
        const leadEventId = `lead-${leadTrackingContext.eventId}`;
        emailSubmittedRef.current = true;
        goNext();
        void (async () => {
          const leadTrackPromise = trackLeadServerEvent({
            email,
            eventId: leadEventId,
            fbp: leadTrackingContext.fbp,
            fbc: leadTrackingContext.fbc
          });
          const [leadSaveResult, leadTrackResult] = await Promise.allSettled([
            saveLeadWithRetry(email, onboardingResponses, {
              marketingConsent: true,
              funnelSessionId,
              personaKey,
              paywallScenario
            }),
            leadTrackPromise
          ]);

          if (leadSaveResult.status === "fulfilled") {
            leadIdRef.current = leadSaveResult.value;
          } else {
            console.warn("[leads] failed to save lead after retries", leadSaveResult.reason);
          }

          if (leadTrackResult.status === "rejected") {
            console.warn("[leads] server-side lead tracking failed", leadTrackResult.reason);
          }

          trackMetaLead(leadEventId);
        })();
        return;
      } finally {
        emailSubmissionPendingRef.current = false;
      }
    }

    goNext();
  };

  const handleSelectSingle = (answerKey: string, optionId: string) => {
    setSingleAnswer(answerKey, optionId);

    try {
      posthog.capture("quiz_answered", {
        funnel_session_id: funnelSessionId,
        funnel_flow: flow,
        welcome_variant: welcomeVariant,
        question_key: answerKey,
        answer_key: optionId
      });
    } catch (trackingError) {
      console.warn("[posthog] quiz_answered not captured", trackingError);
    }

    if (currentStep.kind !== "question" || currentStep.selection !== "single") {
      return;
    }

    const autoAdvanceDelay = currentStep.optionFeedback ? 320 : 70;
    if (autoAdvanceTimeoutRef.current !== null) {
      window.clearTimeout(autoAdvanceTimeoutRef.current);
    }

    autoAdvanceTimeoutRef.current = window.setTimeout(() => {
      autoAdvanceTimeoutRef.current = null;
      goNext();
    }, autoAdvanceDelay);
  };

  const handleWelcomeEmbeddedSelect = (answerKey: string, optionId: string) => {
    setSingleAnswer(answerKey, optionId);
    trackWelcomeViewContent();

    // Flow B asks this inside the welcome step, so emit a synthetic funnel step pageview.
    if (answerKey === "practice_intent") {
      try {
        posthog.capture(
          "$pageview",
          buildEmbeddedStepPageviewPayload(
            window.location.href,
            flow,
            "practice_intent",
            currentIndex + 1
          )
        );
      } catch (trackingError) {
        console.warn("[posthog] embedded practice_intent pageview not captured", trackingError);
      }
    }

    window.setTimeout(() => {
      goNext();
    }, 100);
  };

  return (
    <div className={`app-frame ${isWelcomeStep ? "app-frame-welcome" : ""}`}>
      <FunnelShell
        key={currentStep.id}
        isScrollable={isFunnelContentScrollable}
        topBar={
          isPaymentStep ? null : (
            <TopBar
              canGoBack={canGoBack && currentStep.id !== "checkout_complete"}
              onBack={() => {
                if (autoAdvanceTimeoutRef.current !== null) {
                  window.clearTimeout(autoAdvanceTimeoutRef.current);
                  autoAdvanceTimeoutRef.current = null;
                }
                goBack();
              }}
              sections={funnelSections}
              sectionProgress={sectionProgress}
              isWelcomeStep={isWelcomeStep}
              useWelcomeChrome
            />
          )
        }
        bottomBar={
          shouldShowBottomBar ? (
            <BottomConfirmBar
              label={confirmLabel}
              disabled={confirmDisabled}
              disabledHint={disabledHint}
              onClick={handleConfirm}
              noteAbove={bottomBarNoteAbove}
              noteBelow={bottomBarNoteBelow}
              className={bottomBarClassName}
            />
          ) : undefined
        }
      >
        <div
          key={currentStep.id}
          className={`step-page ${isWelcomeStep ? "step-page-welcome" : ""} ${
            resolvedCurrentStep.kind === "question" ? "step-page-question" : ""
          } ${
            resolvedCurrentStep.kind === "question" &&
            resolvedCurrentStep.selection === "multi"
              ? "step-page-question-multi"
              : ""
          } ${isCenterableQuestionPage ? "step-page-question-centered" : ""}`.trim()}
        >
          <StepRenderer
            step={resolvedCurrentStep}
            answers={answers}
            onSelectSingle={handleSelectSingle}
            onToggleMulti={toggleMultiAnswer}
            onSetText={setTextAnswer}
            onWelcomeFirstImageReadyChange={setIsWelcomeHeroReady}
            onWelcomeEmbeddedSelect={handleWelcomeEmbeddedSelect}
            onLoadingContinue={goNext}
            onPaymentContinue={handleConfirm}
            canContinue={!confirmDisabled}
            isPaymentSubmitting={isStartingCheckout}
            paymentCheckoutUrl={getReadyCheckoutUrl(readyCheckout,
              resolvedCurrentStep.kind === "payment" ? resolvedCurrentStep.plans.find((plan) => plan.id === answers[resolvedCurrentStep.answerKey]) : undefined,
              answers.email, checkoutProvider)}
            paymentErrorMessage={checkoutErrorMessage}
            paymentErrorCode={checkoutErrorCode}
            paymentEmail={typeof answers.email === "string" ? answers.email : ""}
            onPaymentEmailChange={(value) => { setReadyCheckout(null); setTextAnswer("email", value); }}
            paywallPersonalization={paywallPersonalization}
            restoredPlanId={recoveryContext?.planId}
            onPlanSelected={(planId) => {
              setReadyCheckout(null);
              setCheckoutErrorMessage(null);
              posthog.capture("plan_selected", {
                funnel_session_id: funnelSessionId,
                funnel_flow: flow,
                welcome_variant: welcomeVariant,
                plan_id: planId,
                pricing_offer: pricingOffer,
                plan_value: livePlans.find((plan) => plan.id === planId)?.amount,
                plan_currency: livePlans.find((plan) => plan.id === planId)?.currencyCode,
                paywall_variant: paywallVariant,
                paywall_scenario: paywallScenario,
                paywall_entry_point: paywallEntryContext.entryPoint,
                paywall_source: paywallEntryContext.source,
                is_checkout_recovery: Boolean(recoveryContext)
              });
            }}
          />
        </div>
      </FunnelShell>
    </div>
  );
};

export default App;
